/**
 * Which named fields of a form the user changed.
 *
 * defaultValue/defaultSelected cannot tell: setting a hidden input's value also sets its
 * defaultValue, and widgets build their options with or without `selected`. So the form
 * values are recorded on the first pointer or key interaction, once every widget has
 * written its initial state, and compared to the values at submit time.
 */

const CONTROL_SELECTOR = "input,select,textarea";

/** values of a form at the user's first interaction with it */
const initialValues = new WeakMap<HTMLFormElement, Map<string, string>>();

// tagName rather than instanceof, so that the unit test can run on plain objects
function controlValue(control: HTMLElement): string {
  if(control.tagName === "SELECT"){
    return Array.from((control as HTMLSelectElement).selectedOptions).map(option => option.value).join(",");
  }
  if(control.tagName === "TEXTAREA"){
    return (control as HTMLTextAreaElement).value;
  }
  const input = control as HTMLInputElement;
  switch(input.type){
    case "file":
      return String(input.files?.length || 0);
    case "checkbox":
    case "radio":
      return input.checked ? input.value : "";
    default:
      return input.value;
  }
}

/** name -> value of the named controls, repeated names (one input per image...) joined */
export function readValues(form: HTMLFormElement): Map<string, string> {
  const values = new Map<string, string[]>();
  form.querySelectorAll<HTMLElement>(CONTROL_SELECTOR).forEach(control => {
    const name = control.getAttribute("name");
    if(!name) return;
    values.set(name, [...(values.get(name) || []), controlValue(control)]);
  });
  return new Map(Array.from(values, ([name, list]) => [name, list.join("\n")]));
}

/** the names whose value differs between two readValues() results, added and removed ones included */
export function diffValues(initial: Map<string, string>, current: Map<string, string>): string[] {
  const names = new Set([...initial.keys(), ...current.keys()]);
  return Array.from(names).filter(name => initial.get(name) !== current.get(name));
}

/**
 * Lists the names of the fields the user changed. Call it before the form is reset or removed
 * (typically before the API call). A form the user never interacted with has no change.
 */
export function changedFieldNames(form: HTMLFormElement): string[] {
  const initial = initialValues.get(form);
  return initial ? diffValues(initial, readValues(form)) : [];
}

function recordInitialValues(event: Event){
  const form = (event.target as Element)?.closest?.("form");
  if(form && !initialValues.has(form)){
    initialValues.set(form, readValues(form));
  }
}
// capture phase on document: runs before the handlers of the control itself change anything
if(typeof document !== "undefined"){
  document.addEventListener("pointerdown", recordInitialValues, true);
  document.addEventListener("keydown", recordInitialValues, true);
}
